import React, { useEffect, useMemo, useState } from "react";
import {
  CalendarDays,
  ChevronDown,
  ClipboardList,
  Download,
  Edit2,
  RefreshCw,
  Search,
  Settings2,
  Upload,
  UserPlus,
  X,
} from "lucide-react";
import AdminLayout from "../../components/layouts/AdminLayout";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import Select from "../../components/ui/Select";
import { admissionService } from "../../services/admissionService";
import { programService } from "../../services/programService";
import {
  kuccpsTemplateCsv,
  parseKuccpsPlacementFile,
} from "../../utils/kuccpsImport";
import WalkInAdmissionModal from "./components/WalkInAdmissionModal";
import IntakeModal from "./components/IntakeModal";

const statuses = [
  "submitted",
  "under_review",
  "documents_required",
  "qualified",
  "offered",
  "admitted",
  "rejected",
  "withdrawn",
];
const statusStyle = {
  submitted: "bg-blue-100 text-blue-800",
  under_review: "bg-amber-100 text-amber-800",
  documents_required: "bg-orange-100 text-orange-800",
  qualified: "bg-emerald-100 text-emerald-800",
  offered: "bg-green-100 text-green-800",
  admitted: "bg-purple-100 text-purple-800",
  rejected: "bg-red-100 text-red-800",
  withdrawn: "bg-slate-100 text-slate-700",
};

const AdmissionsManagement = ({ readOnly = false, embedded = false }) => {
  const [activeTab, setActiveTab] = useState("applications");
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    status: "all",
    applicationType: "all",
    programId: "all",
    intakeId: "all",
    level: "all",
  });
  const [programs, setPrograms] = useState([]);
  const [configProgram, setConfigProgram] = useState("");
  const [levelConfigs, setLevelConfigs] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState("");
  const [editingRequirements, setEditingRequirements] = useState(null);
  const [showWalkIn, setShowWalkIn] = useState(false);
  const [showKuccpsImport, setShowKuccpsImport] = useState(false);
  const [showAdmissionSettings, setShowAdmissionSettings] = useState(false);
  // Intake management
  const [intakes, setIntakes] = useState([]);
  const [intakesLoading, setIntakesLoading] = useState(false);
  const [intakeModal, setIntakeModal] = useState(null); // null | 'new' | intake-object
  const [intakeSaving, setIntakeSaving] = useState(false);
  const [intakeError, setIntakeError] = useState("");
  const [isIntakesCollapsed, setIsIntakesCollapsed] = useState(false);

  useEffect(() => {
    programService
      .getPrograms()
      .then(setPrograms)
      .catch(() => {});
  }, []);

  const loadIntakes = () => {
    setIntakesLoading(true);
    admissionService
      .getIntakes()
      .then(setIntakes)
      .catch((err) => setIntakeError(err?.message || "Failed to load intakes."))
      .finally(() => setIntakesLoading(false));
  };
  useEffect(() => {
    loadIntakes();
  }, []);
  useEffect(() => {
    if (!configProgram) return setLevelConfigs([]);
    admissionService
      .getLevelConfigurations(configProgram)
      .then((existing) => {
        setLevelConfigs(
          [3, 4, 5, 6, 7].map(
            (level) =>
              existing.find((item) => item.level === level) || {
                program_id: configProgram,
                level,
                program_level_code: "",
                capacity: 0,
                kuccps_capacity: 0,
                self_sponsored_capacity: 0,
                is_active: false,
                minimum_mean_grade: "",
                admission_criteria: { description: "" },
              },
          ),
        );
      })
      .catch((err) =>
        setError(err?.message || "Unable to load admission level settings."),
      );
  }, [configProgram]);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const result = await admissionService.getApplications(filters);
      setApplications(result.data);
    } catch (err) {
      setError(err?.message || "Unable to load admission applications.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [
    filters.status,
    filters.applicationType,
    filters.programId,
    filters.intakeId,
    filters.level,
  ]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return applications.filter((item) => {
      if (filters.programId && filters.programId !== "all") {
        const itemProgId = String(item.program_id || item.program?.id || "");
        if (itemProgId !== String(filters.programId)) return false;
      }
      if (filters.level && filters.level !== "all") {
        const itemLvl = String(item.applied_level || "");
        if (itemLvl !== String(filters.level)) return false;
      }
      if (filters.intakeId && filters.intakeId !== "all") {
        if (String(item.intake_id || item.intake?.id || "") !== String(filters.intakeId)) return false;
      }
      if (!term) return true;
      return [
        item.application_number,
        item.first_name,
        item.last_name,
        item.student_email,
        item.email,
        item.kuccps_placement_reference,
        item.program?.name,
        item.program?.code,
      ].some((value) => value?.toLowerCase().includes(term));
    });
  }, [applications, search, filters.programId, filters.level, filters.intakeId]);

  const updateStatus = async (application, status) => {
    try {
      const updated = await admissionService.updateApplicationStatus(
        application.id,
        status,
        application.review_notes,
      );
      setApplications((current) =>
        current.map((item) =>
          item.id === updated.id ? { ...item, ...updated } : item,
        ),
      );
    } catch (err) {
      setError(err?.message || "Status update failed.");
    }
  };

  const reviewDocuments = async (application) => {
    try {
      const documents = await admissionService.getApplicationDocuments(
        application.id,
      );
      if (documents.length === 0)
        return setError(
          "No academic documents were uploaded for this application.",
        );
      const url = await admissionService.getDocumentUrl(
        documents[0].storage_path,
      );
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setError(err?.message || "Unable to open the academic document.");
    }
  };

  // Keep the summary cards aligned with the records currently shown in the
  // table. Using the unfiltered application array here made the cards report
  // institution-wide totals after a programme, intake, level, or search
  // filter had been applied.
  const totals = visible.reduce(
    (result, item) => {
      result.total += 1;
      result[item.application_type] = (result[item.application_type] || 0) + 1;
      if (item.status === "offered") result.offered += 1;
      if (item.application_type === "self_sponsored") {
        if (item.status === "admitted") result.selfAdmitted += 1;
        else if (item.status === "rejected") result.selfRejected += 1;
        else if (
          [
            "submitted",
            "under_review",
            "documents_required",
            "qualified",
            "offered",
          ].includes(item.status)
        )
          result.selfPending += 1;
      }
      return result;
    },
    {
      total: 0,
      kuccps: 0,
      self_sponsored: 0,
      offered: 0,
      selfAdmitted: 0,
      selfPending: 0,
      selfRejected: 0,
    },
  );

  const saveLevel = async (config) => {
    try {
      const kuccpsCapacity = Number(config.kuccps_capacity) || 0,
        selfCapacity = Number(config.self_sponsored_capacity) || 0;
      const saved = await admissionService.saveLevelConfiguration({
        ...config,
        capacity: kuccpsCapacity + selfCapacity,
        kuccps_capacity: kuccpsCapacity,
        self_sponsored_capacity: selfCapacity,
        minimum_mean_grade: config.minimum_mean_grade || null,
      });
      setLevelConfigs((current) =>
        current.map((item) => (item.level === saved.level ? saved : item)),
      );
      return saved;
    } catch (err) {
      setError(err?.message || "Unable to save level configuration.");
    }
  };

  const importKuccps = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setError("");
    setImportResult("");
    try {
      const rows = await parseKuccpsPlacementFile(file);
      const result = await admissionService.importKuccpsPlacements({
        fileName: file.name,
        placementYear: new Date().getFullYear(),
        rows,
        programs,
      });
      setImportResult(
        `${result.imported} KUCCPS placements imported successfully.`,
      );
    } catch (err) {
      setError(err?.message || "KUCCPS import failed.");
    } finally {
      setImporting(false);
      event.target.value = "";
    }
  };

  const saveIntake = async (form) => {
    setIntakeSaving(true);
    try {
      if (!intakeModal || intakeModal === "new") return;
      const updated = await admissionService.updateIntake(intakeModal.id, form);
      setIntakes((prev) => prev.map((item) => (item.id === updated.id ? { ...item, ...updated } : item)));
      setIntakeModal(null);
    } finally {
      setIntakeSaving(false);
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([kuccpsTemplateCsv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "kuccps-placement-template.csv";
    anchor.click();
    URL.revokeObjectURL(url);
  };

  // ── Intake management panel ─────────────────────────────────────────────
  const intakePanel = (
    <div className="space-y-3">
      {/* Single-line Minimalistic Header with Collapse Control */}
      <div
        onClick={() => setIsIntakesCollapsed(!isIntakesCollapsed)}
        className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200/80 bg-white p-3 shadow-sm cursor-pointer select-none hover:border-slate-300 transition-colors"
      >
        <div className="flex min-w-0 items-center gap-2.5">
          <ChevronDown
            className={`h-4 w-4 text-slate-500 transition-transform duration-200 ${isIntakesCollapsed ? "-rotate-90" : ""}`}
          />
          <h2 className="text-sm font-bold tracking-tight text-slate-900">
            Admission Intakes
          </h2>
          <span className="text-xs text-slate-500 font-mono">
            · {intakes.length} intakes
          </span>
        </div>

        <div
          className="flex flex-wrap items-center justify-end gap-2"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={() => setIsIntakesCollapsed(!isIntakesCollapsed)}
            className="flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-md border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition-colors shadow-2xs"
          >
            <span>
              {isIntakesCollapsed ? "Expand Intakes" : "Collapse Intakes"}
            </span>
          </button>

          {!readOnly && (
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-xs font-semibold"
              onClick={loadIntakes}
              disabled={intakesLoading}
            >
              <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${intakesLoading ? "animate-spin" : ""}`} /> Sync academic periods
            </Button>
          )}
        </div>
      </div>

      {intakeError && (
        <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">
          {intakeError}
        </div>
      )}

      {!isIntakesCollapsed && (
        <>
          {intakesLoading ? (
            <p className="text-sm text-slate-400 p-4">Loading intakes…</p>
          ) : intakes.length === 0 ? (
            <div className="rounded-xl border border-dashed p-10 text-center text-slate-400">
              <CalendarDays className="mx-auto mb-3 h-8 w-8" />
              <p>
                No current or future academic periods are configured. Add them in the Academic Calendar, then sync this list.
              </p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border bg-white shadow-sm">
              <table className="w-full text-sm">
                <thead className="hidden border-b bg-slate-50 sm:table-header-group">
                  <tr>
                    {[
                      "Academic period",
                      "Academic year",
                      "App. window",
                      "Status",
                      "",
                    ].map((h) => (
                      <th
                        key={h}
                        className="px-4 py-3 text-left font-semibold text-slate-600"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {intakes.map((intake) => (
                    <tr key={intake.id} className="block border-b p-3 last:border-b-0 hover:bg-slate-50 sm:table-row sm:border-0 sm:p-0">
                      <td className="block px-3 py-2 font-medium sm:table-cell sm:px-4 sm:py-3">
                        <span className="mr-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400 sm:hidden">Academic period</span>
                        {intake.semester_ref?.name || intake.name}
                        <span className="ml-2 font-mono text-xs text-slate-400">{intake.code}</span>
                      </td>
                      <td className="block px-3 py-2 text-slate-500 sm:table-cell sm:px-4 sm:py-3">
                        <span className="mr-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400 sm:hidden">Academic year</span>
                        {intake.academic_year_ref?.name || intake.academic_year}
                      </td>
                      <td className="block px-3 py-2 text-xs text-slate-500 sm:table-cell sm:px-4 sm:py-3">
                        <span className="mr-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400 sm:hidden">Application window</span>
                        {intake.application_opens_at
                          ? new Date(
                              intake.application_opens_at,
                            ).toLocaleDateString("en-KE")
                          : "—"}
                        {" → "}
                        {intake.application_closes_at
                          ? new Date(
                              intake.application_closes_at,
                            ).toLocaleDateString("en-KE")
                          : "—"}
                      </td>
                      <td className="block px-3 py-2 sm:table-cell sm:px-4 sm:py-3">
                        <span className="mr-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400 sm:hidden">Status</span>
                        <span
                          className={`inline-block rounded-full px-2.5 py-1 text-xs font-medium ${intake.is_active && (!intake.application_closes_at || new Date(intake.application_closes_at) >= new Date()) ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}
                        >
                          {intake.is_active && (!intake.application_closes_at || new Date(intake.application_closes_at) >= new Date()) ? "Open" : "Closed"}
                        </span>
                      </td>
                      {!readOnly && (
                        <td className="block px-3 py-2 sm:table-cell sm:px-4 sm:py-3">
                          <span className="mr-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400 sm:hidden">Actions</span>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setIntakeModal(intake)}
                              className="rounded p-1 text-slate-400 hover:text-blue-600 hover:bg-blue-50"
                              title="Edit"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );

  const content = (
    <div className={embedded ? "space-y-3" : "space-y-6"}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        {embedded ? (
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <h1 className="mr-2 text-sm font-bold text-foreground">Admissions</h1>
            {[["applications", "Applications"], ...(!readOnly ? [["intakes", "Intakes"]] : [])].map(([key, label]) => (
              <button key={key} onClick={() => setActiveTab(key)} className={`rounded-md px-3 py-1.5 text-xs font-medium ${activeTab === key ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"}`}>
                {label}
              </button>
            ))}
            {activeTab === "applications" && (
              <>
                <span className="ml-1 text-xs text-muted-foreground">{totals.total} applications</span>
                <span className="text-xs text-muted-foreground">· {totals.selfPending} pending</span>
                <span className="text-xs text-muted-foreground">· {totals.offered} offers</span>
              </>
            )}
          </div>
        ) : (
          <div>
            <h1 className="text-2xl font-bold">Admissions</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {readOnly
                ? "View applications for programmes within your assigned academic scope."
                : "Manage intakes, applications and admission decisions."}
            </p>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          {activeTab === "applications" && !readOnly && (
            <>
              <Button onClick={() => setShowWalkIn(true)}>
                <UserPlus className="h-4 w-4 mr-2" />
                Walk-in student
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowAdmissionSettings(true)}
              >
                <Settings2 className="h-4 w-4 mr-2" />
                Programme-level settings
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowKuccpsImport(true)}
              >
                <Upload className="h-4 w-4 mr-2" />
                Import KUCCPS placements
              </Button>
            </>
          )}
          {activeTab === "applications" ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={load}
              disabled={loading}
              title="Refresh applications"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              onClick={loadIntakes}
              disabled={intakesLoading}
              title="Refresh intakes"
            >
              <RefreshCw className={`h-4 w-4 ${intakesLoading ? "animate-spin" : ""}`} />
            </Button>
          )}
        </div>
      </div>
      {/* Tabs */}
      {!embedded && <div className="flex border-b">
        {[
          ["applications", "Applications"],
          ...(!readOnly ? [["intakes", "Intakes"]] : []),
        ].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`px-5 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === key
                ? "border-primary text-primary"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            {label}
          </button>
        ))}
      </div>}
      {activeTab === "intakes" && intakePanel}
      {activeTab === "applications" && <>
      {!embedded && <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {[
          ["All applications", totals.total],
          ["KUCCPS", totals.kuccps],
        ].map(([label, value]) => (
          <div key={label} className="rounded-xl border bg-card p-5 shadow-sm">
            <p className="text-sm text-muted-foreground">{label}</p>
            <p className="text-3xl font-bold mt-1">{value}</p>
          </div>
        ))}
        <div className="rounded-xl border bg-card p-5 shadow-sm sm:col-span-2">
          <div className="flex items-baseline justify-between gap-3">
            <p className="text-sm text-muted-foreground">Self-sponsored</p>
            <p className="text-3xl font-bold text-foreground">{totals.self_sponsored}</p>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-2 border-t border-slate-100 pt-3 text-center sm:grid-cols-3">
            <div className="min-w-0">
              <p className="text-lg font-bold text-emerald-700">{totals.selfAdmitted}</p>
              <p className="truncate text-[11px] text-muted-foreground">Admitted</p>
            </div>
            <div className="min-w-0">
              <p className="text-lg font-bold text-amber-700">{totals.selfPending}</p>
              <p className="truncate text-[11px] text-muted-foreground">Pending</p>
            </div>
            <div className="min-w-0">
              <p className="text-lg font-bold text-red-700">{totals.selfRejected}</p>
              <p className="truncate text-[11px] text-muted-foreground">Rejected</p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border bg-card p-5 shadow-sm">
          <p className="text-sm text-muted-foreground">Offers issued</p>
          <p className="text-3xl font-bold mt-1">{totals.offered}</p>
        </div>
      </div>}
      {showKuccpsImport && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-xl bg-white shadow-xl">
            <div className="flex items-start justify-between border-b p-5">
              <div>
                <h2 className="text-lg font-semibold">
                  Import official KUCCPS placements
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Upload the placement export from the KUCCPS Institution
                  Portal.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowKuccpsImport(false)}
                className="rounded-md p-1 text-muted-foreground hover:bg-muted"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-5">
              <p className="mb-4 text-sm text-muted-foreground">
                Accepted formats: CSV and Excel.
              </p>
              <div className="flex flex-wrap gap-3">
                <label className="inline-flex h-10 cursor-pointer items-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground">
                  <Upload className="mr-2 h-4 w-4" />
                  {importing ? "Importing…" : "Choose placement file"}
                  <input
                    type="file"
                    accept=".csv,.xlsx"
                    className="hidden"
                    onChange={importKuccps}
                    disabled={importing}
                  />
                </label>
                <Button variant="outline" onClick={downloadTemplate}>
                  <Download className="h-4 w-4 mr-2" />
                  Download template
                </Button>
              </div>
              {importResult && (
                <p className="mt-4 rounded-md bg-green-50 p-3 text-sm text-green-700">
                  {importResult}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
      {showAdmissionSettings && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[92vh] w-full max-w-6xl overflow-y-auto rounded-xl bg-white shadow-xl">
            <div className="sticky top-0 z-10 flex items-start justify-between border-b bg-white p-5">
              <div>
                <h2 className="text-lg font-semibold">
                  Programme-level admission settings
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Set level availability, intake capacity, and entry
                  requirements.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowAdmissionSettings(false)}
                className="rounded-md p-1 text-muted-foreground hover:bg-muted"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <Select
                label="Programme"
                value={configProgram}
                onChange={setConfigProgram}
                searchable
                options={programs.map((item) => ({
                  value: item.id,
                  label: `${item.code ? `${item.code} — ` : ""}${item.name || item.program_name}`,
                }))}
                placeholder="Select programme to configure"
              />
              {configProgram && (
                <div className="grid lg:grid-cols-5 gap-3">
                  {levelConfigs.map((config, index) => (
                    <div
                      key={config.level}
                      className={`rounded-lg border p-4 ${config.is_active ? "border-green-300 bg-green-50/40" : "bg-slate-50"}`}
                    >
                      <div className="flex justify-between items-center">
                        <b>Level {config.level}</b>
                        <input
                          type="checkbox"
                          checked={config.is_active}
                          onChange={(e) =>
                            setLevelConfigs((current) =>
                              current.map((item, itemIndex) =>
                                itemIndex === index
                                  ? { ...item, is_active: e.target.checked }
                                  : item,
                              ),
                            )
                          }
                        />
                      </div>
                      <label className="text-xs block mt-3">
                        Programme level code
                      </label>
                      <Input
                        value={config.program_level_code || ""}
                        readOnly
                        className="bg-muted"
                        placeholder="Set in Edit Programme"
                      />
                      <label className="text-xs block mt-3">
                        KUCCPS allocation
                      </label>
                      <Input
                        type="number"
                        min="0"
                        value={config.kuccps_capacity || 0}
                        onChange={(e) =>
                          setLevelConfigs((current) =>
                            current.map((item, itemIndex) =>
                              itemIndex === index
                                ? { ...item, kuccps_capacity: e.target.value }
                                : item,
                            ),
                          )
                        }
                      />
                      <label className="text-xs block mt-3">
                        Self-sponsored allocation
                      </label>
                      <Input
                        type="number"
                        min="0"
                        value={config.self_sponsored_capacity || 0}
                        onChange={(e) =>
                          setLevelConfigs((current) =>
                            current.map((item, itemIndex) =>
                              itemIndex === index
                                ? {
                                    ...item,
                                    self_sponsored_capacity: e.target.value,
                                  }
                                : item,
                            ),
                          )
                        }
                      />
                      <div className="mt-3 rounded-md bg-white/70 border p-2 text-xs min-h-16">
                        <b>Entry requirements</b>
                        <p className="mt-1 text-muted-foreground line-clamp-2">
                          {config.admission_criteria?.qualification ||
                            config.admission_criteria?.description ||
                            "Not configured"}
                        </p>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="w-full mt-3"
                        onClick={() =>
                          setEditingRequirements({
                            ...config,
                            admission_criteria: config.admission_criteria || {},
                          })
                        }
                      >
                        Configure requirements
                      </Button>
                      <Button
                        size="sm"
                        className="w-full mt-2"
                        onClick={() => saveLevel(config)}
                      >
                        Save level
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Single-line Minimalistic Filter Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200/80 bg-white p-3 shadow-sm xl:flex-nowrap">
        <div className="flex min-w-0 items-center gap-2">
          <h3 className="text-sm font-bold tracking-tight text-slate-900">
            Applications
          </h3>
          <span className="text-xs text-slate-500 font-mono">
            · {visible.length} records
          </span>
        </div>

        <div className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-2 xl:flex-nowrap">
          <div className="relative min-w-[min(100%,240px)] flex-1 xl:w-[240px] xl:flex-none">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              className="h-8 pl-8 text-xs bg-white"
              placeholder="Search applicant or application no."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <Select
            value={filters.programId || "all"}
            onChange={(value) =>
              setFilters((current) => ({ ...current, programId: value }))
            }
            options={[
              { value: "all", label: "All Programmes" },
              ...programs.map((p) => ({
                value: p.id,
                label: p.code
                  ? `${p.code} - ${p.name || p.program_name}`
                  : p.name || p.program_name,
              })),
            ]}
            className="w-full min-w-[min(100%,220px)] text-xs xl:w-[220px] xl:flex-none"
          />

          <Select
            value={filters.intakeId || "all"}
            onChange={(value) =>
              setFilters((current) => ({ ...current, intakeId: value }))
            }
            options={[
              { value: "all", label: "All Intakes" },
              ...intakes.map((intake) => ({
                value: intake.id,
                label: `${intake.name || intake.code}${intake.code && intake.name ? ` (${intake.code})` : ""}`,
              })),
            ]}
            className="w-full min-w-[min(100%,220px)] text-xs xl:w-[220px] xl:flex-none"
          />

          <Select
            value={filters.level || "all"}
            onChange={(value) =>
              setFilters((current) => ({ ...current, level: value }))
            }
            options={[
              { value: "all", label: "All Levels" },
              { value: "3", label: "Level 3" },
              { value: "4", label: "Level 4" },
              { value: "5", label: "Level 5" },
              { value: "6", label: "Level 6" },
              { value: "7", label: "Level 7" },
            ]}
            className="w-full min-w-[min(100%,110px)] text-xs sm:w-[110px] sm:flex-none"
          />

          <Select
            value={filters.applicationType}
            onChange={(value) =>
              setFilters((current) => ({ ...current, applicationType: value }))
            }
            options={[
              { value: "all", label: "All channels" },
              { value: "kuccps", label: "KUCCPS" },
              { value: "self_sponsored", label: "Self-sponsored" },
            ]}
            className="w-full min-w-[min(100%,120px)] text-xs sm:w-[120px] sm:flex-none"
          />

          <Select
            value={filters.status}
            onChange={(value) =>
              setFilters((current) => ({ ...current, status: value }))
            }
            options={[
              { value: "all", label: "All statuses" },
              ...statuses.map((value) => ({
                value,
                label: value.replaceAll("_", " "),
              })),
            ]}
            className="w-full min-w-[min(100%,125px)] text-xs sm:w-[125px] sm:flex-none"
          />
        </div>
      </div>
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
          {error}
        </div>
      )}
      <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                {[
                  "Application",
                  "Applicant",
                  "Programme / Level",
                  "Channel / Intake",
                  "Placement / KCSE",
                  "Status",
                ].map((label) => (
                  <th key={label} className="px-5 py-3 text-left font-semibold">
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr>
                  <td
                    colSpan="6"
                    className="p-10 text-center text-muted-foreground"
                  >
                    Loading applications…
                  </td>
                </tr>
              ) : visible.length === 0 ? (
                <tr>
                  <td colSpan="6" className="p-10 text-center">
                    <ClipboardList className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                    <p>No applications found.</p>
                  </td>
                </tr>
              ) : (
                visible.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50">
                    <td className="px-5 py-4">
                      <p className="font-mono font-medium">
                        {item.application_number}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {item.submitted_at || item.created_at
                          ? new Date(
                              item.submitted_at || item.created_at,
                            ).toLocaleDateString("en-KE")
                          : "Date unavailable"}
                      </p>
                    </td>
                    <td className="px-5 py-4">
                      <p className="font-medium">
                        {item.first_name} {item.middle_name} {item.last_name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {item.student_email || item.email} · {item.phone_number}
                      </p>
                      {item.student_email && (
                        <p className="text-[11px] text-muted-foreground">
                          Backup: {item.email}
                        </p>
                      )}
                    </td>
                    <td className="px-5 py-4 min-w-52">
                      <p className="font-medium">
                        {item.program?.name || "Programme not mapped"}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {item.program?.code || "No code"} ·{" "}
                        {item.applied_level
                          ? `Level ${item.applied_level}`
                          : "Level not specified"}
                      </p>
                    </td>
                    <td className="px-5 py-4">
                      <p className="font-medium capitalize">
                        {item.application_type === "self_sponsored"
                          ? "Self-sponsored"
                          : item.application_type || "—"}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {item.intake?.name || item.intake?.code || "Intake not assigned"}
                      </p>
                    </td>
                    <td className="px-5 py-4">
                      <p>
                        {item.kuccps_placement_reference || item.kcse_index_number || "—"}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {item.kcse_mean_grade
                          ? `Mean grade ${item.kcse_mean_grade}`
                          : ""}
                      </p>
                      {item.documents?.some(
                        (document) => document.security_status === "rejected",
                      ) && (
                        <p className="mt-2 block rounded bg-red-100 px-2 py-1 text-xs font-semibold text-red-800">
                          Security rejection — do not open
                        </p>
                      )}
                      {item.documents?.some(
                        (document) => document.requires_manual_review,
                      ) && (
                        <p className="mt-2 inline-block rounded bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-800">
                          Manual document verification required
                        </p>
                      )}
                      {item.documents?.some(
                        (document) => document.applicant_edited_fields?.length,
                      ) && (
                        <p className="mt-2 block rounded bg-red-100 px-2 py-1 text-xs font-semibold text-red-800">
                          Red flag: applicant edited extracted evidence (
                          {[
                            ...new Set(
                              item.documents.flatMap(
                                (document) =>
                                  document.applicant_edited_fields || [],
                              ),
                            ),
                          ]
                            .join(", ")
                            .replaceAll("_", " ")}
                          )
                        </p>
                      )}
                      {item.application_type === "self_sponsored" &&
                        !item.documents?.some(
                          (document) => document.security_status === "rejected",
                        ) && (
                          <button
                            type="button"
                            onClick={() => reviewDocuments(item)}
                            className="mt-2 block text-xs font-medium text-blue-700 hover:underline"
                          >
                            Review academic document
                          </button>
                        )}
                    </td>
                    <td className="px-5 py-4 min-w-48">
                      <span
                        className={`inline-block mb-2 rounded-full px-2.5 py-1 text-xs font-medium ${statusStyle[item.status]}`}
                      >
                        {item.status.replaceAll("_", " ")}
                      </span>
                      {item._studentOnly ? (
                        <p className="text-xs text-muted-foreground">
                          Managed in Student Management
                        </p>
                      ) : readOnly ? (
                        <p className="text-xs text-muted-foreground">
                          View only
                        </p>
                      ) : (
                        <Select
                          value={item.status}
                          onChange={(value) => updateStatus(item, value)}
                          options={statuses.map((value) => ({
                            value,
                            label: value.replaceAll("_", " "),
                          }))}
                        />
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      </>}
      {editingRequirements && (
        <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between">
              <div>
                <h2 className="text-xl font-bold">
                  Level {editingRequirements.level} entry requirements
                </h2>
                <p className="text-sm text-muted-foreground">
                  Configure what prospective candidates will see.
                </p>
              </div>
              <button
                onClick={() => setEditingRequirements(null)}
                className="text-2xl"
              >
                ×
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <Input
                  label="Minimum KCSE mean grade"
                  value={editingRequirements.minimum_mean_grade || ""}
                  onChange={(e) =>
                    setEditingRequirements((current) => ({
                      ...current,
                      minimum_mean_grade: e.target.value,
                    }))
                  }
                  placeholder="e.g. C-"
                />
                <Input
                  label="Primary qualification"
                  value={
                    editingRequirements.admission_criteria?.qualification || ""
                  }
                  onChange={(e) =>
                    setEditingRequirements((current) => ({
                      ...current,
                      admission_criteria: {
                        ...current.admission_criteria,
                        qualification: e.target.value,
                      },
                    }))
                  }
                  placeholder="e.g. KCSE or equivalent"
                />
              </div>
              <div>
                <label className="text-sm font-medium">
                  General requirement
                </label>
                <textarea
                  className="mt-2 w-full min-h-24 rounded-md border p-3 text-sm"
                  value={
                    editingRequirements.admission_criteria?.description || ""
                  }
                  onChange={(e) =>
                    setEditingRequirements((current) => ({
                      ...current,
                      admission_criteria: {
                        ...current.admission_criteria,
                        description: e.target.value,
                      },
                    }))
                  }
                  placeholder="Describe the overall entry requirement."
                />
              </div>
              <div>
                <label className="text-sm font-medium">
                  Subject requirements
                </label>
                <textarea
                  className="mt-2 w-full min-h-20 rounded-md border p-3 text-sm"
                  value={
                    editingRequirements.admission_criteria
                      ?.subject_requirements || ""
                  }
                  onChange={(e) =>
                    setEditingRequirements((current) => ({
                      ...current,
                      admission_criteria: {
                        ...current.admission_criteria,
                        subject_requirements: e.target.value,
                      },
                    }))
                  }
                  placeholder="e.g. Minimum D+ in Mathematics and English"
                />
              </div>
              <div>
                <label className="text-sm font-medium">
                  Alternative qualifications
                </label>
                <textarea
                  className="mt-2 w-full min-h-20 rounded-md border p-3 text-sm"
                  value={
                    editingRequirements.admission_criteria
                      ?.alternative_qualifications || ""
                  }
                  onChange={(e) =>
                    setEditingRequirements((current) => ({
                      ...current,
                      admission_criteria: {
                        ...current.admission_criteria,
                        alternative_qualifications: e.target.value,
                      },
                    }))
                  }
                  placeholder="Certificates, transcripts, recognition of prior learning, or equivalent qualifications."
                />
              </div>
              <div>
                <label className="text-sm font-medium">
                  Required supporting documents
                </label>
                <textarea
                  className="mt-2 w-full min-h-20 rounded-md border p-3 text-sm"
                  value={
                    editingRequirements.admission_criteria
                      ?.required_documents || ""
                  }
                  onChange={(e) =>
                    setEditingRequirements((current) => ({
                      ...current,
                      admission_criteria: {
                        ...current.admission_criteria,
                        required_documents: e.target.value,
                      },
                    }))
                  }
                  placeholder="e.g. KCSE result slip, certificate and transcript"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Additional notes</label>
                <textarea
                  className="mt-2 w-full min-h-16 rounded-md border p-3 text-sm"
                  value={editingRequirements.admission_criteria?.notes || ""}
                  onChange={(e) =>
                    setEditingRequirements((current) => ({
                      ...current,
                      admission_criteria: {
                        ...current.admission_criteria,
                        notes: e.target.value,
                      },
                    }))
                  }
                />
              </div>
            </div>
            <div className="p-6 border-t flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setEditingRequirements(null)}
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  const saved = await saveLevel(editingRequirements);
                  if (saved) setEditingRequirements(null);
                }}
              >
                Save requirements
              </Button>
            </div>
          </div>
        </div>
      )}
      {showWalkIn && (
        <WalkInAdmissionModal
          programs={programs}
          onClose={() => setShowWalkIn(false)}
          onAdmitted={() => {
            setShowWalkIn(false);
            load();
          }}
        />
      )}
      {intakeModal && (
        <IntakeModal
          intake={intakeModal === "new" ? null : intakeModal}
          onSave={saveIntake}
          onClose={() => setIntakeModal(null)}
          saving={intakeSaving}
        />
      )}
    </div>
  );

  return embedded ? (
    content
  ) : (
    <AdminLayout title="Admissions Management">{content}</AdminLayout>
  );
};

export default AdmissionsManagement;
